//
// # Report-an-Issue client helper
//
// IDE-side wrapper around AlphaServer's `POST /report-issue` endpoint
// (see issue #2146). The modal and the command-palette entry will both
// call this; keeping it isolated lets us unit-test the wire path without
// dragging in the modal component.
//
// The endpoint sits behind the JWT auth middleware. We attach the same
// `Authorization: Bearer <id-token>` the rest of the IDE's authenticated
// API calls do. If `getToken` isn't wired (e.g. local dev with auth
// disabled) we send the call without it; the server enforces auth.
//
// Errors are best-effort surfaced to the caller as a `Result` so the
// modal can render an actionable toast (rate-limited, invalid body,
// server error, network error).
//

import { Result as CoreResult, type Result } from "CoreUtils";
import {
  IssueReport,
  IssueReportResponse,
  type IssueReport as IssueReportType,
  type IssueReportResponse as IssueReportResponseType,
} from "ServerInterface";

const REPORT_ISSUE_PATH = "/report-issue";
const REPORT_ISSUE_TIMEOUT_MS = 15_000;

export interface PostReportIssueArgs {
  alphaServerUrl: string;
  // Same shape as AlphaClient's getToken: returns a Firebase ID token or
  // null/undefined if no signed-in user. Optional so dev/test paths
  // without auth still work.
  getToken?: () => Promise<string | null>;
}

export type PostReportIssueError =
  | { why: "rate-limited" }
  | { why: "invalid-body" }
  | { why: "auth-required" }
  | { why: "server-error"; status: number }
  | { why: "network-error"; message: string }
  | { why: "invalid-response" };

/**
 * POST an IssueReport to AlphaServer's /report-issue endpoint.
 *
 * Result.Ok carries the server-returned IssueReportResponse, including
 * the trackingUrl (a GitHub issue URL on success, or the sentinel
 * "logged-only" if the GitHub sink wasn't configured / failed). Either
 * way the report is at minimum in our server logs.
 *
 * Result.Err is a PostReportIssueError the caller can switch on to pick
 * a user-facing toast. Never throws.
 */
export async function postReportIssue(
  args: PostReportIssueArgs,
  report: IssueReportType
): Promise<Result<IssueReportResponseType, PostReportIssueError>> {
  // Validate locally too — a 400 from the server is wasted round-trip
  // and a worse error to render than "we caught it client-side".
  const localValidate = IssueReport.validator.validate(report);
  if (localValidate.err) {
    return CoreResult.Err({ why: "invalid-body" });
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (args.getToken) {
    const token = await args.getToken();
    if (token !== null) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }

  const controller = new globalThis.AbortController();
  const timer = globalThis.setTimeout(() => {
    controller.abort("report-issue request timed out");
  }, REPORT_ISSUE_TIMEOUT_MS);

  let httpResponse: Response;
  try {
    httpResponse = await fetch(`${args.alphaServerUrl}${REPORT_ISSUE_PATH}`, {
      method: "POST",
      headers,
      body: JSON.stringify(report),
      signal: controller.signal,
    });
  } catch (err) {
    return CoreResult.Err({
      why: "network-error",
      message: err instanceof Error ? err.message : String(err),
    });
  } finally {
    globalThis.clearTimeout(timer);
  }

  if (httpResponse.status === 401) {
    return CoreResult.Err({ why: "auth-required" });
  }
  if (httpResponse.status === 400) {
    return CoreResult.Err({ why: "invalid-body" });
  }
  if (httpResponse.status === 429) {
    return CoreResult.Err({ why: "rate-limited" });
  }
  if (!httpResponse.ok) {
    return CoreResult.Err({ why: "server-error", status: httpResponse.status });
  }

  let parsed: unknown;
  try {
    parsed = await httpResponse.json();
  } catch {
    return CoreResult.Err({ why: "invalid-response" });
  }
  const validated = IssueReportResponse.validator.validate(parsed);
  if (validated.err) {
    return CoreResult.Err({ why: "invalid-response" });
  }
  return CoreResult.Ok(validated.val);
}
